/**
 * Gettext translator pro JS/React — čte STEJNÉ .mo katalogy jako NetteTranslator
 * na PHP straně (front/app/lang/{lang}.front.mo), takže překlady jsou jen na
 * jednom místě a msgid v komponentách jsou tytéž řetězce jako v Latte {_'...'}.
 *
 * - binární .mo se stáhne jednou (endpoint /api/v1/translations/front.mo
 *   s immutable cache + ?v=mtime) a parsuje se přímo v prohlížeči
 * - podpora plurálů podle Plural-Forms hlavičky katalogu
 * - chybějící překlad vrací msgid (stejné chování jako NetteTranslator)
 */

export type TranslateFn = (message: string, count?: number) => string;

interface Catalog {
    entries: Map<string, string[]>;
    pluralFn: (n: number) => number;
}

const MO_MAGIC_LE = 0x950412de;
const MO_MAGIC_BE = 0xde120495;

/** Rozparsuje binární .mo soubor na mapu msgid → [překlady (plurální formy)]. */
export function parseMo(buffer: ArrayBuffer): Catalog {
    const view = new DataView(buffer);
    const magic = view.getUint32(0, true);

    let littleEndian: boolean;
    if (magic === MO_MAGIC_LE) {
        littleEndian = true;
    } else if (view.getUint32(0, false) === MO_MAGIC_LE) {
        littleEndian = false;
    } else if (magic === MO_MAGIC_BE) {
        littleEndian = false;
    } else {
        throw new Error('Invalid .mo file (bad magic number)');
    }

    const u32 = (offset: number) => view.getUint32(offset, littleEndian);

    const count = u32(8);
    const originalsOffset = u32(12);
    const translationsOffset = u32(16);

    const decoder = new TextDecoder('utf-8');
    const readString = (tableOffset: number, index: number): string => {
        const length = u32(tableOffset + index * 8);
        const offset = u32(tableOffset + index * 8 + 4);
        return decoder.decode(new Uint8Array(buffer, offset, length));
    };

    const entries = new Map<string, string[]>();
    let headers = '';

    for (let i = 0; i < count; i++) {
        const original = readString(originalsOffset, i);
        const translation = readString(translationsOffset, i);

        if (original === '') {
            headers = translation;
            continue;
        }

        // kontext (msgctxt\x04msgid) se v projektu nepoužívá — případně odřízne
        const contextSplit = original.split('');
        const ids = contextSplit[contextSplit.length - 1];
        // msgid s plurálem je "singulár\0plurál" — klíčujeme singulárem
        const singular = ids.split(' ')[0];

        entries.set(singular, translation.split(' '));
    }

    return { entries, pluralFn: compilePluralFn(headers) };
}

/**
 * Zkompiluje výraz z Plural-Forms hlavičky (např. "(n==1) ? 0 : (n>=2 && n<=4 ? 1 : 2)")
 * na funkci. Výraz se validuje na povolené znaky, žádný obecný eval.
 */
function compilePluralFn(headers: string): (n: number) => number {
    const match = headers.match(/Plural-Forms:\s*nplurals\s*=\s*\d+\s*;\s*plural\s*=\s*([^;\n]+)/i);
    if (!match) {
        return (n) => (n === 1 ? 0 : 1);
    }

    const expression = match[1].trim();
    if (!/^[n0-9\s()?:%!&|<>=+*/-]+$/.test(expression)) {
        return (n) => (n === 1 ? 0 : 1);
    }

    try {
        const fn = new Function('n', `return Number(${expression});`) as (n: number) => number;
        fn(1); // smoke test

        return fn;
    } catch {
        return (n) => (n === 1 ? 0 : 1);
    }
}

/** Translator nad naparsovaným katalogem. */
export function createTranslator(catalog: Catalog): TranslateFn {
    return (message: string, count?: number): string => {
        const forms = catalog.entries.get(message);
        if (!forms || forms.length === 0 || forms[0] === '') {
            return message;
        }

        if (count === undefined) {
            return forms[0];
        }

        const index = Math.max(0, Math.min(catalog.pluralFn(count), forms.length - 1));

        return forms[index] ?? forms[0];
    };
}

/** Identita — když katalog neexistuje/nejde načíst (chování jako NetteTranslator). */
export const identityTranslator: TranslateFn = (message) => message;

const translatorCache = new Map<string, Promise<TranslateFn>>();

/**
 * Stáhne a rozparsuje .mo katalog; výsledek se drží v paměti per URL
 * (HTTP cache řeší immutable hlavičky endpointu + ?v= verze v URL).
 */
export function loadTranslator(url: string): Promise<TranslateFn> {
    let cached = translatorCache.get(url);
    if (!cached) {
        cached = fetch(url, { credentials: 'same-origin' })
            .then(async (response) => {
                if (!response.ok) {
                    return identityTranslator;
                }
                return createTranslator(parseMo(await response.arrayBuffer()));
            })
            .catch(() => identityTranslator);
        translatorCache.set(url, cached);
    }

    return cached;
}
